import { useCallback } from 'react';

import type { ClientStorageItem } from '@/shared/api/clientStorage';

import { useMutation, useQuery } from '@tanstack/react-query';
import { create } from 'zustand';

import { API } from '@/shared/api';
import { queryClient } from '@/shared/config';

import { CLIENT_STORAGE_QUERY_KEYS } from '../lib/constants';

type UseClientStorageItemOptions = {
  enabled?: boolean;
  staleTime?: number;
};

type UseClientStorageItemResult = {
  data: ClientStorageItem | null;
  isDeleting: boolean;
  isError: boolean;
  isLoading: boolean;
  isNotFound: boolean;
  isSetting: boolean;
  isSuccess: boolean;
  isWriteError: boolean;
  deleteItem: () => void;
  setItem: (value: string) => void;
  updateItem: (apply: ApplyUpdate) => void;
};

type ApplyUpdate = (current: string | undefined) => string;

type UpdateItemVariables = { apply: ApplyUpdate; epoch: number };

const useWriteErrorStore = create<Record<string, boolean>>(() => ({}));
const failedUpdates = new Map<string, ApplyUpdate>();
let writeEpoch = 0;

export const resetClientStorageWriteErrors = () => {
  writeEpoch += 1;
  failedUpdates.clear();
  useWriteErrorStore.setState({}, true);
};

const withFailedUpdate = (key: string, apply: ApplyUpdate): ApplyUpdate => {
  const failed = failedUpdates.get(key);

  return failed ? (current) => apply(failed(current)) : apply;
};

const isOnlyPendingWrite = (scopeId: string) =>
  queryClient.getMutationCache().findAll({
    predicate: (mutation) => mutation.options.scope?.id === scopeId,
    status: 'pending',
  }).length <= 1;

const getItemOrNull = async (key: string) => {
  try {
    return await API.getItem(key);
  } catch (error) {
    const apiError = error as { status?: number };
    if (apiError.status === 404) {
      return null;
    }
    throw error;
  }
};

export const useClientStorageItem = (
  key: string,
  options: UseClientStorageItemOptions = {},
): UseClientStorageItemResult => {
  const { enabled = true, staleTime } = options;
  const itemKey = CLIENT_STORAGE_QUERY_KEYS.item(key);
  const scope = { id: `clientStorage:${key}` };

  const query = useQuery({
    enabled: enabled && Boolean(key),
    queryFn: () => getItemOrNull(key),
    queryKey: CLIENT_STORAGE_QUERY_KEYS.item(key),
    retry: (failureCount, error) => {
      const apiError = error as { status?: number };
      if (apiError.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
    staleTime,
  });

  const setMutation = useMutation({
    mutationFn: (value: string) => API.setItem(key, value),
    mutationKey: CLIENT_STORAGE_QUERY_KEYS.setItem,
    scope,
    onSuccess: (data) => {
      queryClient.setQueryData(CLIENT_STORAGE_QUERY_KEYS.item(data.key), data);
      void queryClient.invalidateQueries({ queryKey: CLIENT_STORAGE_QUERY_KEYS.list });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ apply, epoch }: UpdateItemVariables): Promise<ClientStorageItem | null> => {
      const isCurrent = () => epoch === writeEpoch && queryClient.getQueryData(itemKey) !== undefined;

      if (!isCurrent()) return null;

      const current = await getItemOrNull(key);

      return isCurrent() ? API.setItem(key, withFailedUpdate(key, apply)(current?.value)) : null;
    },
    mutationKey: CLIENT_STORAGE_QUERY_KEYS.setItem,
    retry: 2,
    scope,
    onError: (_error, { apply, epoch }) => {
      if (epoch !== writeEpoch) return;

      failedUpdates.set(key, withFailedUpdate(key, apply));
      useWriteErrorStore.setState({ [key]: true });
    },
    onSuccess: (data, { epoch }) => {
      if (!data) return;

      if (epoch === writeEpoch) {
        failedUpdates.delete(key);
        useWriteErrorStore.setState({ [key]: false });
        if (isOnlyPendingWrite(scope.id) && queryClient.getQueryData(itemKey) !== undefined) {
          queryClient.setQueryData(itemKey, data);
        }
      }
      void queryClient.invalidateQueries({ queryKey: CLIENT_STORAGE_QUERY_KEYS.list });
    },
  });
  const { mutate: setMutate } = setMutation;
  const { mutate: updateMutate } = updateMutation;
  const setItem = useCallback((value: string) => setMutate(value), [setMutate]);
  const updateItem = useCallback(
    (apply: ApplyUpdate) => {
      const queryKey = CLIENT_STORAGE_QUERY_KEYS.item(key);
      const cached = queryClient.getQueryData<ClientStorageItem | null>(queryKey);
      const now = new Date().toISOString();

      void queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<ClientStorageItem>(queryKey, {
        key,
        value: apply(cached?.value),
        created_at: cached?.created_at ?? now,
        updated_at: now,
      });
      updateMutate({ apply, epoch: writeEpoch });
    },
    [key, updateMutate],
  );
  const isWriteError = useWriteErrorStore((state) => Boolean(state[key]));

  const deleteMutation = useMutation({
    mutationFn: () => API.deleteItem(key),
    mutationKey: CLIENT_STORAGE_QUERY_KEYS.deleteItem,
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: CLIENT_STORAGE_QUERY_KEYS.item(key) });
      void queryClient.invalidateQueries({ queryKey: CLIENT_STORAGE_QUERY_KEYS.list });
    },
  });

  return {
    data: query.data ?? null,
    isDeleting: deleteMutation.isPending,
    isError: query.isError,
    isLoading: query.isLoading,
    isNotFound: query.data === null,
    isSetting: setMutation.isPending || updateMutation.isPending,
    isSuccess: query.isSuccess,
    isWriteError,
    deleteItem: deleteMutation.mutate,
    setItem,
    updateItem,
  };
};
